<script>
import { iButton, iButtonSizes, iDropzone, showToast } from '@inscribe/signal';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import CreateCollectSessionModal from '@/V2/collect/CreateCollectSessionModal';
import * as documentStates from '@/constants/documentStates';
import { showDuplicateUploadToast } from '@/utils/notifications';
import { registerChatAgentHookClickEvent } from '@/events-logger';

export default {
  components: {
    CreateCollectSessionModal,
    iDropzone,
    iButton,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      required: false,
    },
    expanded: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showCollectModal: false,
      filesBeingDragged: false,
      iButtonSizes,
      setHoverTimeout: null,
    };
  },
  computed: {
    ...mapGetters([
      'collectEnabled',
      'isUploadAccessBlocked',
      'isChatAgentEnabled',
    ]),
    ...mapState({
      currentUser: (state) => state.currentUser,
      selectedCustomer: (state) => state.customers.selectedCustomer,
    }),
    shouldShowUploadButton() {
      return (
        this.selectedCustomer?.organisation?.id ===
        this.currentUser?.organisation?.id
      );
    },
    documents() {
      return this.$store.state.documents;
    },
  },
  mounted() {
    this.setupDocumentListDropzone('mount');
  },
  beforeUnmount() {
    this.setupDocumentListDropzone('unmount');
  },
  methods: {
    ...mapActions(['uploadCustomerFiles', 'toggleChat']),
    ...mapMutations([
      'addDocument',
      'setDocuments',
      'invalidateDocumentCacheByID',
    ]),
    startUpload() {
      const dropzoneElement = this.$refs.dropzone;
      dropzoneElement && dropzoneElement.onZoneClick();
    },
    preventRefreshWhileUploading(event) {
      event.preventDefault();
      event.returnValue = '';
    },
    setFilesBeingDragged(value) {
      this.filesBeingDragged = value;
    },
    onInvalidFileDropped(file) {
      this.setFilesBeingDragged(false);
      showToast({
        title: 'Unsupported file type',
        isError: true,
        text: 'The selected file format is not supported',
      });
    },
    onFilesSelectedForUpload(files) {
      this.$emit('files-selected-for-upload', files);
      this.setFilesBeingDragged(false);
      window.addEventListener(
        'beforeunload',
        this.preventRefreshWhileUploading,
      );

      this.uploadingDocuments = files.map((f) => {
        return {
          name: f.name,
          type: f.type,
          size: f.size,
          created_at: new Date().toISOString(),
          state: documentStates.UPLOADING,
          uploadProgress: 0,
          urls: {},
          uploadId: crypto.randomUUID(),
        };
      });

      for (const document of this.uploadingDocuments) {
        this.addDocument(document);
      }
      this.invalidateDocumentCacheByID(this.selectedCustomer?.id);
      this.uploadCustomerFiles({
        customerId: this.selectedCustomer?.id || this.$route.params.customer_id,
        tags: this.selectedTags,
        files,
        uploadIds: JSON.stringify(
          this.uploadingDocuments.map((document) => document.uploadId),
        ),
      })
        .then((response) => {
          this.onUploadSuccess(response.data);
        })
        .catch((err) => {
          this.onUploadError(err);
        })
        .finally(() => {
          window.removeEventListener(
            'beforeunload',
            this.preventRefreshWhileUploading,
          );
          this.uploadingDocuments.splice(0);
        });
    },
    onUploadSuccess(data) {
      // update the ids of documents
      const newDocuments = [...this.documents];
      data.metadata.upload_ids.forEach((uploadId, index) => {
        const foundIndex = newDocuments.findIndex(
          (document) => document.uploadId === uploadId,
        );
        if (foundIndex > -1) {
          newDocuments[foundIndex] = {
            ...newDocuments[foundIndex],
            ...data.data[index],
          };
        }
      });
      this.setDocuments(newDocuments);
      this.$emit('files-uploaded', newDocuments);
      showToast({
        text: 'Your documents have been uploaded and are now being processed.',
      });
    },
    onUploadError(err) {
      this.setDocuments(
        this.documents.filter((document) => document.state !== 'UPLOADING'),
      );
      if (err.status == 409) {
        showDuplicateUploadToast(err.response.data.duplicates);
      } else {
        showToast({
          text: 'There was an error uploading one or more files.',
          isError: true,
        });
      }
    },
    setupDocumentListDropzone(hook) {
      const dropzoneEl = this.$refs.dropzone?.$el;
      if (dropzoneEl) {
        const fn =
          hook === 'mount'
            ? dropzoneEl.addEventListener
            : dropzoneEl.removeEventListener;
        fn('dragenter', this.dragHandlerFunction, false);
        fn('dragleave', this.dragHandlerFunction, false);
        fn('dragover', this.dragHandlerFunction, false);
        fn('drop', this.dragHandlerFunction, false);
      }
    },
    dragHandlerFunction(evt) {
      if (this.setHoverTimeout) {
        clearTimeout(this.setHoverTimeout);
      }
      const isDragging = ['dragover', 'dragenter'].includes(evt.type);
      if (isDragging) {
        this.setFilesBeingDragged(true);
      } else {
        this.setHoverTimeout = setTimeout(() => {
          this.setFilesBeingDragged(false);
        }, 1000);
      }
      evt.preventDefault();
      evt.stopPropagation();
    },
    onOpenAssistantClicked() {
      this.toggleChat();
      registerChatAgentHookClickEvent('documents-title-upload-open-assistant');
    },
  },
};
</script>

<template>
  <div class="documents-title d-flex flex-column">
    <!-- header -->
    <div
      class="header d-flex justify-content-between align-items-center i-mb-11"
    >
      <div class="d-flex flex-column">
        <span class="i-text-heading-5 i-text-neutral-120 title">{{
          title
        }}</span>
        <span class="i-text-body-2 i-mt-1">{{ subtitle }}</span>
      </div>
      <div class="d-flex align-items-center">
        <i-button
          v-if="isChatAgentEnabled"
          text="Open Assistant"
          v-read-only-check
          type="neutral"
          subtype="outline"
          :size="iButtonSizes.MEDIUM"
          aria-label="Open Assistant"
          data-testid="open-assistant-button"
          class="i-mr-4"
          @button-click="onOpenAssistantClicked"
        />

        <i-button
          v-if="shouldShowUploadButton && collectEnabled"
          text="Collect"
          v-read-only-check
          type="neutral"
          subtype="outline"
          :size="iButtonSizes.MEDIUM"
          aria-label="Create Collect Session"
          data-testid="create-collect-session-button"
          class="i-mr-4"
          :disabled="isUploadAccessBlocked"
          @button-click="showCollectModal = true"
        />

        <i-button
          v-if="shouldShowUploadButton"
          data-testid="upload-button"
          text="Upload"
          v-read-only-check
          type="primary"
          subtype="strong"
          :size="iButtonSizes.MEDIUM"
          :disabled="isUploadAccessBlocked"
          @button-click="startUpload"
        ></i-button>

        <create-collect-session-modal
          v-if="!isUploadAccessBlocked"
          :show="showCollectModal"
          :selected-customer="selectedCustomer"
          @hide="showCollectModal = false"
        />
      </div>
    </div>

    <slot name="before-dropzone"></slot>

    <!-- dropzone -->
    <i-dropzone
      ref="dropzone"
      :class="{
        expanded: expanded || filesBeingDragged,
        'i-mt-11': expanded || filesBeingDragged,
      }"
      :hidden-mode="!expanded && !filesBeingDragged"
      title="Drag and drop documents to upload"
      sub-title="PDFs, JPEGs and PNGs accepted"
      :accepted-extensions="['pdf', 'jpg', 'jpeg', 'png']"
      :disabled="isUploadAccessBlocked"
      @files-selected="onFilesSelectedForUpload"
      @invalid-file-type-detected="onInvalidFileDropped"
    ></i-dropzone>
  </div>
</template>

<style lang="scss" scoped>
.documents-title {
  .i-dropzone {
    padding-top: 0px;
    padding-bottom: 0px;

    &.expanded {
      height: 300px;
      margin-bottom: 40px;
    }
  }
}
</style>
